import { ElMessageBox } from "element-plus";
import axios from "axios";
import { getToken } from "@/utils/auth";
import { usePermissionStore } from "@/stores";
import { permissionMpping } from "@/utils/permission";

export const showMessageBox = (opts, callback, cancelCallback: any = null) => {
  ElMessageBox.confirm(opts.message, opts.title, {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: opts.type,
  })
    .then(() => {
      typeof callback === "function" && callback();
    })
    .catch((e) => {
      typeof cancelCallback === "function" && cancelCallback(e);
      console.log("取消操作", e);
    });
};

/**
 * 校验是否为正数
 * @param input
 */
export function validatePositiveNumber(input) {
  const regex = /^\d+(\.\d{1,})?$/;
  return regex.test(input);
}

/**
 * 校验是否为正整数
 */
export function validatePositiveInteger(input) {
  const regex = /^\d+$/;
  return regex.test(input);
}

/**
 * 校验是否问英文或者数字
 */
export function validateEnglishOrNumber(input) {
  const regex = /^[a-zA-Z0-9]+$/;
  return regex.test(input);
}

/**
 * 表单整数校验
 * @param message
 * @param isRequired
 */
export const validateIntegerFormRules = (
  message: string,
  isRequired: boolean = true,
) => {
  return (rule: any, value: any, callback: any) => {
    if (!isRequired && !value) {
      callback();
      return;
    }
    if (validatePositiveInteger(value)) {
      callback();
    } else {
      callback(new Error(message));
    }
  };
};

export const isPrototypeObject = (obj: any) =>
  Object.prototype.toString.call(obj) === "[object Object]";

/**
 * 初始化对象数据
 * @param initValObject 初始值对象
 * @param origin 目标对象
 * @returns
 */
export const setObjectInitialize = <T>(initValObject: T, origin: T): void => {
  if (!isPrototypeObject(initValObject)) return;
  for (let [key, value] of Object.entries(initValObject)) {
    origin[key] = value;
  }
};

export const toFormData = (params: any) => {
  const formData = new FormData();
  Object.keys(params).forEach((key) => {
    formData.append(key, params[key]);
  });
  return formData;
};

/**
 * 添加url参数
 * @param url 更新的url
 * @param params 参数数组
 */
export const addUrlParam = ({ url = "", params }) => {
  if (!Array.isArray(params) || params.length === 0) return;
  const replaceUrl = new URL(url || window.location);
  params.forEach((item) => {
    replaceUrl.searchParams.set(item.key, item.value);
  });
  history.replaceState(null, "", replaceUrl);
};

// 格式化日期为 "YYYY-MM-DD HH:mm:ss" 格式
export function formatDateToCustomString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * toFixed方法重写解决精度丢失，替换原型链上的方法
 */
type NumOrStr = number | string;
Number.prototype.toFixed = function (d: any) {
  let changeNum: NumOrStr = this + ""; // 把数字转为字符串
  if (changeNum.indexOf("-") != -1) {
    // 判断是否是负数
    changeNum = Math.abs(changeNum as any);
  }
  changeNum = (
    Math.round((changeNum as any) * Math.pow(10, d)) / Math.pow(10, d)
  ).toString();

  let index = changeNum.indexOf("."); // 判断字符是否有小数点
  if (index == -1) {
    // 如果没有小数点，加上对应个数的小数点
    changeNum += ".";
    changeNum += new Array(d + 1).join("0");
  } else if (index > 0) {
    // 如果有，index是小数点的坐标值，比如在第二位index=1
    let temp = d - (changeNum.length - index - 1); // 小数点后的位数小于要取舍的个数，用0补齐；数字的长度-小数点的坐标值-1=小数的位数(小数点也占以为，4.00 length:4;.的index是1)
    if (temp > 0) {
      changeNum += new Array(temp + 1).join("0");
    }
  }
  if (this.toString().indexOf("-") != -1) {
    // 如果是负数，加上-
    changeNum = "-" + changeNum;
  }
  return changeNum;
};
/**
 * 通用文件下载方法
 * @param {string} url - 请求地址
 * @param {Object} params - 请求参数
 * @param {string} method - 请求方法 (默认: POST)
 */
export async function downloadFile({
  url,
  params = {},
  method = "POST",
  headers = {},
  name = "商品列表",
}) {
  try {
    // 发起请求，设置 responseType 为 'blob'
    const response = await axios({
      url,
      method,
      data: method === "POST" ? params : undefined, // 仅 POST 时传递请求体
      params: method === "GET" ? params : undefined, // 仅 GET 时传递查询参数
      headers: {
        ...headers, // 合并传入的 headers
        authorization: getToken(), // 添加 auth 参数
      },
    });
    if (response.data.code != 200) {
      ElMessage.error(response.data.msg);
      return;
    }
    const data = window.atob(response.data.data);
    const len = data.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = data.charCodeAt(i);
    }
    // 创建 Blob 对象
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // 从响应头中获取文件名
    const disposition = response.headers["content-disposition"];
    const filename = disposition
      ? decodeURIComponent(
          disposition.split("filename=")[1]?.replace(/['"]/g, ""),
        )
      : name + ".xlsx"; // 默认文件名
    // 创建下载链接并触发下载
    const urlObject = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = urlObject;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // 释放 Blob URL
    window.URL.revokeObjectURL(urlObject);
  } catch (error) {
    console.error("文件下载失败:", error);
    throw error; // 抛出错误，便于外部处理
  }
}

/**
 * 添加样式规则
 * @returns
 */
export function addStyleRules({ styleObject, className }): void {
  const oStyle: any =
    document.querySelector(`.${className}`) ?? createStyleSheetNode(className);
  if (!oStyle) return;
  for (const [name, rules] of Object.entries(styleObject)) {
    if ((oStyle.sheet || {}).insertRule) {
      oStyle.sheet.insertRule(`${name}{${rules}}`, oStyle.length);
    } else {
      (oStyle.styleSheet || oStyle.sheet).addRule(
        `${name}`,
        rules,
        oStyle.length,
      );
    }
  }
}

/**
 * 创建style标签
 * @returns
 */
export function createStyleSheetNode(className): Node {
  const styleNode = document.createElement("style");
  styleNode.type = "text/css";
  styleNode.setAttribute("class", `${className}`);
  document.getElementsByTagName("head")[0].appendChild(styleNode);
  return styleNode;
}

/**
 * 获取url 所有参数
 */
export function getQueryParams(): Record<string, string> {
  const url = window.location.href;
  const params = new URL(url).searchParams;
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

/**
 * 获取url 指定参数
 */
export function getQueryParamsByKey(key: string): string {
  const url = window.location.href;
  const params = new URL(url).searchParams;
  return params.get(key) ?? "";
}

/**
 * @description 限制日期控件 选择范围
 * @param date
 */
export function validateTimeRange(date: Date): Date[] {
  if (!date || date.length !== 2) return;

  let [startDate, endDate] = date;

  // 计算最大允许的结束日期（开始日期 + 31 天）
  const maxEndDate = new Date(startDate);
  maxEndDate.setDate(maxEndDate.getDate() + 31); // 增加 31 天

  // 如果结束日期超出了 31 天范围，则重置为最大允许日期
  if (new Date(endDate).getTime() > new Date(maxEndDate).getTime()) {
    date[1] = formatDateToCustomString(maxEndDate);
  }

  return date;
}

/**
 * @description 系统权限控制
 */
export function systemPermissionControl(permissionKey: string): boolean {
  const permissionStore = usePermissionStore();
  return (
    permissionStore.btnAuthArr?.indexOf(permissionMpping[permissionKey]) !== -1
  );
}

export default {};
